import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LifeBuoy, ShieldCheck, X, Eye } from "lucide-react";
import { API, useApp } from "../../context";

const isAdmin = (u) => u?.role === "admin" || u?.role === "super_admin";

function countdown(expIso) {
  const ms = new Date(expIso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Shown to the ADMIN while they're inside a live support session (overlay active).
function AdminSessionBar() {
  const [sess, setSess] = useState(null);
  const [tick, setTick] = useState(0);
  const { refreshUser } = useApp();

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/support/current`);
      setSess(r.data.active ? r.data : null);
    } catch { setSess(null); }
  }, []);

  useEffect(() => { poll(); const i = setInterval(poll, 8000); return () => clearInterval(i); }, [poll]);
  useEffect(() => { const i = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(i); }, []);

  const exit = async () => {
    try { await axios.post(`${API}/support/exit`); } catch {}
    setSess(null);
    toast.success("Support session ended");
    try { await refreshUser(); } catch {}
    window.location.href = "/admin-dashboard/people";
  };

  if (!sess) return null;
  return (
    <div className="w-full bg-amber-500 text-black px-4 py-2 flex items-center justify-between gap-3 text-sm font-bold" data-testid="support-admin-bar">
      <span className="flex items-center gap-2 min-w-0">
        <Eye size={15} /> <span className="truncate">Support view — you are seeing <b>{sess.target_name}</b>'s account. Changes affect their account.</span>
      </span>
      <span className="flex items-center gap-3 shrink-0">
        <span className="tabular-nums bg-black/15 rounded-full px-2 py-0.5">{countdown(sess.expires_at)}{tick && ""}</span>
        <button onClick={exit} className="bg-black text-amber-300 rounded-full px-3 py-1 flex items-center gap-1" data-testid="support-admin-exit"><X size={13} /> End session</button>
      </span>
    </div>
  );
}

// Shown to a regular USER: approve a pending request, and/or see + revoke an active session.
function UserConsentBar() {
  const [inbox, setInbox] = useState(null);
  const busy = useRef(false);

  const poll = useCallback(async () => {
    try { const r = await axios.get(`${API}/support/inbox`); setInbox(r.data); } catch { setInbox(null); }
  }, []);
  useEffect(() => { poll(); const i = setInterval(poll, 6000); return () => clearInterval(i); }, [poll]);

  const respond = async (approve) => {
    if (busy.current || !inbox?.pending_request) return;
    busy.current = true;
    try {
      await axios.post(`${API}/support/respond`, { request_id: inbox.pending_request.request_id, approve });
      toast[approve ? "success" : "message"](approve ? "Support can now view your account" : "Request declined");
      poll();
    } catch (e) { toast.error("Could not respond", { description: e.response?.data?.detail || e.message }); }
    finally { busy.current = false; }
  };

  const revoke = async () => {
    try { await axios.post(`${API}/support/revoke`); toast.success("Support access ended"); poll(); }
    catch (e) { toast.error("Could not end access", { description: e.response?.data?.detail || e.message }); }
  };

  if (inbox?.pending_request) {
    return (
      <div className="w-full bg-indigo-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="support-consent-request">
        <span className="flex items-center gap-2 min-w-0"><LifeBuoy size={16} /> <span className="truncate"><b>{inbox.pending_request.admin_name}</b> from Isaiah's World support is asking to view your account to help you.</span></span>
        <span className="flex items-center gap-2 shrink-0">
          <button onClick={() => respond(true)} className="bg-white text-indigo-700 font-bold rounded-full px-3 py-1" data-testid="support-consent-approve">Allow</button>
          <button onClick={() => respond(false)} className="bg-indigo-800 text-white rounded-full px-3 py-1" data-testid="support-consent-deny">Not now</button>
        </span>
      </div>
    );
  }
  if (inbox?.active_session) {
    return (
      <div className="w-full bg-emerald-600 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="support-active-bar">
        <span className="flex items-center gap-2 min-w-0"><ShieldCheck size={15} /> <span className="truncate">Isaiah's World support ({inbox.active_session.admin_name}) is viewing your account to help.</span></span>
        <button onClick={revoke} className="bg-white text-emerald-700 font-bold rounded-full px-3 py-1 shrink-0" data-testid="support-active-revoke">End access</button>
      </div>
    );
  }
  return null;
}

export default function SupportBanner() {
  const { user } = useApp();
  if (!user) return null;
  // When an admin is overlaid into a user (support_viewing), show the admin bar.
  if (user.support_viewing || isAdmin(user)) return <AdminSessionBar />;
  return <UserConsentBar />;
}
